import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Chip,
  IconButton,
  Rating,
  Divider,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedIcon from '@mui/icons-material/Verified';
import CachedIcon from '@mui/icons-material/Cached';
import Navbar from './common/Navbar';
import Footer from './common/Footer';
import Breadcrumb from './common/Breadcrumb';
import Loader from './common/Loader';
import ProductCard from './common/ProductCard';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { getProductById, getAllProducts } from '../services/api';
import { formatPrice, calculateDiscount } from '../utils/formatPrice';

const weightVariants = ['250g', '500g', '1kg'];

const Viewproduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, addToWishlist, removeFromWishlist, isInWishlist, isInCart } = useCart();
  const { isAuthenticated, openLoginModal } = useAuth();

  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState('250g');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [currentMRP, setCurrentMRP] = useState(0);

  const inWishlist = product ? isInWishlist(product._id) : false;
  const inCart = product ? isInCart(product._id) : false;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [productRes, allProductsRes] = await Promise.all([
          getProductById(id),
          getAllProducts(),
        ]);

        const productData = productRes.data.data;
        setProduct(productData);

        // Set initial price based on ProductPricing or fallback to ProductPrice
        if (productData.ProductPricing && productData.ProductPricing['250g']) {
          setCurrentPrice(productData.ProductPricing['250g'].price);
          setCurrentMRP(productData.ProductPricing['250g'].mrp);
        } else {
          setCurrentPrice(productData.ProductPrice);
          setCurrentMRP(productData.ProductMRP);
        }

        // Get related products from same category
        const allProducts = allProductsRes.data.data || [];
        const categoryName = productData.ProductCategory?.CategoryName || productData.ProductCategory;
        const related = allProducts
          .filter((p) => {
            const pCat = p.ProductCategory?.CategoryName || p.ProductCategory;
            return pCat === categoryName && p._id !== productData._id;
          })
          .slice(0, 4);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    setQuantity(1);
  }, [id]);

  // Update price when selected variant changes
  useEffect(() => {
    if (product && product.ProductPricing && product.ProductPricing[selectedVariant]) {
      setCurrentPrice(product.ProductPricing[selectedVariant].price);
      setCurrentMRP(product.ProductPricing[selectedVariant].mrp);
    } else if (product) {
      // Fallback to default price if no pricing for this variant
      setCurrentPrice(product.ProductPrice);
      setCurrentMRP(product.ProductMRP);
    }
  }, [selectedVariant, product]);

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    // Pass product with current price based on selected weight
    addToCart({
      ...product,
      selectedVariant,
      ProductPrice: currentPrice,
      ProductMRP: currentMRP
    }, quantity);
  };

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    // Pass product with current price based on selected weight
    addToCart({
      ...product,
      selectedVariant,
      ProductPrice: currentPrice,
      ProductMRP: currentMRP
    }, quantity);
    navigate('/cart');
  };

  const handleWishlistToggle = () => {
    if (!isAuthenticated) {
      openLoginModal();
      return;
    }
    if (inWishlist) {
      removeFromWishlist(product._id);
    } else {
      addToWishlist(product);
    }
  };

  if (loading) {
    return <Loader message="Loading product..." />;
  }

  if (!product) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
        <Navbar />
        <Container sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h5">Product not found</Typography>
          <Button onClick={() => navigate('/')} sx={{ mt: 2 }}>
            Go Home
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  const discount = calculateDiscount(currentMRP, currentPrice);
  const categoryName = product.ProductCategory?.CategoryName || product.ProductCategory;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#faf8f5' }}>
      <Navbar />

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: 'Shop', path: '/filter/All' },
            { label: categoryName, path: `/filter/${categoryName}` },
            { label: product.ProductName },
          ]}
        />

        <Grid container spacing={4}>
          {/* Product Image */}
          <Grid item xs={12} md={5}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: '#fff',
                position: 'relative',
              }}
            >
              {discount > 0 && (
                <Chip
                  label={`${discount}% OFF`}
                  sx={{
                    position: 'absolute',
                    top: 16,
                    left: 16,
                    bgcolor: '#e53935',
                    color: '#fff',
                    fontWeight: 'bold',
                  }}
                />
              )}
              <IconButton
                onClick={handleWishlistToggle}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  bgcolor: 'rgba(255,255,255,0.9)',
                }}
              >
                {inWishlist ? (
                  <FavoriteIcon sx={{ color: '#e53935' }} />
                ) : (
                  <FavoriteBorderIcon />
                )}
              </IconButton>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 400,
                }}
              >
                <img
                  src={product.ProductMainImgUrl}
                  alt={product.ProductName}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 400,
                    objectFit: 'contain',
                    borderRadius: 8,
                  }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* Product Details */}
          <Grid item xs={12} md={7}>
            <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 3 }}>
              <Typography variant="h4" fontWeight="bold" color="#3E2723" gutterBottom>
                {product.ProductName}
              </Typography>

              {/* Rating */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Rating value={4.5} precision={0.5} readOnly size="small" />
                <Typography variant="body2" color="text.secondary">
                  4.5 (128 reviews)
                </Typography>
              </Box>

              {/* Price */}
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 3 }}>
                <Typography variant="h3" fontWeight="bold" color="#2e7d32">
                  Rs.{formatPrice(currentPrice)}
                </Typography>
                {currentMRP > currentPrice && (
                  <>
                    <Typography
                      variant="h5"
                      sx={{ textDecoration: 'line-through', color: '#999' }}
                    >
                      Rs.{formatPrice(currentMRP)}
                    </Typography>
                    <Chip
                      label={`Save Rs.${formatPrice(currentMRP - currentPrice)}`}
                      size="small"
                      sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
                    />
                  </>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Short Description */}
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {product.ProductShortDesc}
              </Typography>

              {/* Weight Variants */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Select Weight
                </Typography>
                <ToggleButtonGroup
                  value={selectedVariant}
                  exclusive
                  onChange={(e, val) => val && setSelectedVariant(val)}
                  sx={{ gap: 1 }}
                >
                  {weightVariants.map((variant) => (
                    <ToggleButton
                      key={variant}
                      value={variant}
                      sx={{
                        px: 3,
                        py: 1,
                        borderRadius: '8px !important',
                        border: '1px solid #ddd !important',
                        '&.Mui-selected': {
                          bgcolor: '#8B4513',
                          color: '#fff',
                          '&:hover': { bgcolor: '#6d3610' },
                        },
                      }}
                    >
                      {variant}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              {/* Quantity */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Quantity
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    sx={{ border: '1px solid #ddd' }}
                  >
                    <RemoveIcon />
                  </IconButton>
                  <Typography
                    sx={{
                      px: 3,
                      py: 1,
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      minWidth: 60,
                      textAlign: 'center',
                    }}
                  >
                    {quantity}
                  </Typography>
                  <IconButton
                    onClick={() => setQuantity(quantity + 1)}
                    sx={{ border: '1px solid #ddd' }}
                  >
                    <AddIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                <Button
                  variant={inCart ? 'outlined' : 'contained'}
                  size="large"
                  startIcon={<ShoppingCartIcon />}
                  onClick={handleAddToCart}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    bgcolor: inCart ? 'transparent' : '#8B4513',
                    color: inCart ? '#8B4513' : '#fff',
                    borderColor: '#8B4513',
                    '&:hover': {
                      bgcolor: inCart ? 'rgba(139,69,19,0.1)' : '#6d3610',
                    },
                  }}
                >
                  {inCart ? 'Added to Cart' : 'Add to Cart'}
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleBuyNow}
                  sx={{
                    flex: 1,
                    py: 1.5,
                    bgcolor: '#2e7d32',
                    '&:hover': { bgcolor: '#1b5e20' },
                  }}
                >
                  Buy Now
                </Button>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Features */}
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <LocalShippingIcon sx={{ color: '#8B4513', fontSize: 32 }} />
                    <Typography variant="body2" fontWeight="bold">
                      Free Delivery
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      On orders above Rs.499
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <VerifiedIcon sx={{ color: '#8B4513', fontSize: 32 }} />
                    <Typography variant="body2" fontWeight="bold">
                      100% Authentic
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Premium quality
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <CachedIcon sx={{ color: '#8B4513', fontSize: 32 }} />
                    <Typography variant="body2" fontWeight="bold">
                      Easy Returns
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      7 days return
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Product Description */}
            <Box sx={{ bgcolor: '#fff', p: 3, borderRadius: 3, mt: 3 }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Product Details
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                {product.ProductLongDesc || product.ProductShortDesc}
              </Typography>

              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Category
                </Typography>
                <Chip
                  label={categoryName}
                  onClick={() => navigate(`/filter/${categoryName}`)}
                  sx={{ cursor: 'pointer' }}
                />
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <Box sx={{ mt: 6 }}>
            <Typography variant="h5" fontWeight="bold" color="#3E2723" gutterBottom>
              You May Also Like
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              {relatedProducts.map((p) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  key={p._id}
                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <ProductCard product={p} />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>

      <Footer />
    </Box>
  );
};

export default Viewproduct;
